import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getRequest } from "../../../services/api";

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { formatAppDate } from "../../../helpers/date-formatter";
import {
  CheckCircleIcon,
  CodeBracketIcon,
  QrCodeIcon,
} from "@heroicons/react/16/solid";
import {
  approveRedeemCode,
  searchRedeemCode,
} from "../../../services/billing-invoice/offer-redeem";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
};

const getDaywiseDataset = (weeklyData) => {
  const labels = weeklyData.map((data) => data.date).reverse();
  const datasets = weeklyData.map((data) => data.amount).reverse();

  const data = {
    labels,
    datasets: [
      {
        label: "Amount (€)",
        data: datasets,
        backgroundColor: "rgba(21, 128, 61, 0.9)",
      },
    ],
  };

  return data;
};

const getMilestoneUserDataset = (milestoneData) => {
  const labels = milestoneData.map((data) => `€${data.targetAmount}`);
  const datasets = milestoneData.map((data) => data.count);

  const data = {
    labels,
    datasets: [
      {
        label: "User count",
        data: datasets,
        backgroundColor: "rgba(210, 28, 61, 0.9)",
      },
    ],
  };

  return data;
};

function Home() {
  const [monthlyTransaction, setMonthlyTransaction] = useState("");
  const [monthlyTransactionCount, setMonthlyTransactionCount] = useState(null);
  const [monthlyRedeemAmount, setMonthlyRedeemAmount] = useState("");
  const [monthlyRedeemCount, setMonthlyRedeemCount] = useState(null);
  const [daywiseTransactionDataset, setDaywiseTransactionDataset] =
    useState(null);
  const [milestoneUserDataset, setMilestoneUserDataset] = useState(null);

  const [error, setError] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemList, setRedeemList] = useState([]);

  useEffect(() => {
    getMonthlyTransaction();

    getMonthlyTransactionCount();

    getMonthlyRedeemAmount();

    getMonthlyRedeemCount();

    getTransactionsInDays(6);

    getUserCountByMilestone();
  }, []);

  const getMonthlyTransaction = async () => {
    try {
      const response = await getRequest(
        "admin/data/invoices/transaction-amount",
        true
      );
      setMonthlyTransaction(response.total);
    } catch (error) {
      console.log(error);
    }
  };

  const getMonthlyRedeemAmount = async () => {
    try {
      const response = await getRequest(
        "admin/data/invoices/redeem-amount",
        true
      );
      setMonthlyRedeemAmount(response.total);
    } catch (error) {
      console.log(error);
    }
  };

  const getMonthlyTransactionCount = async () => {
    try {
      const response = await getRequest(
        "admin/data/invoices/transaction-count",
        true
      );
      setMonthlyTransactionCount(response.count);
    } catch (error) {
      console.log(error);
    }
  };

  const getMonthlyRedeemCount = async () => {
    try {
      const response = await getRequest(
        "admin/data/invoices/redeem-count",
        true
      );
      setMonthlyRedeemCount(response.count);
    } catch (error) {
      console.log(error);
    }
  };

  const getTransactionsInDays = async (days) => {
    try {
      const response = await getRequest(
        "admin/data/invoices/daily-transaction-amount-days?days=" + days,
        true
      );
      setDaywiseTransactionDataset(getDaywiseDataset(response));
    } catch (error) {
      console.log(error);
    }
  };

  const getUserCountByMilestone = async () => {
    try {
      const response = await getRequest(
        `admin/data/invoices/milestone-users`,
        true
      );
      setMilestoneUserDataset(getMilestoneUserDataset(response));
    } catch (error) {
      console.log(error);
    }
  };

  const handleRedeemCodeChange = useCallback((e) => {
    setRedeemCode(e.target.value);
    setError(null);
  }, []);

  const handleRedeemCodeSearch = useCallback(async () => {
    if (redeemCode.trim() === "") {
      setError("Enter redeem code to search");
      return;
    }

    try {
      setError(null);
      const response = await searchRedeemCode(redeemCode);

      if (response.length == 0) {
        setError("Redeem code already used or not found.");
      } else {
        setRedeemList(response);
      }
    } catch (error) {
      console.log(error);
    }
  }, [redeemCode]);

  const redeemAmount = useMemo(() => {
    if (redeemList.length > 0) {
      return redeemList.reduce((acc, curr) => acc + curr.receivableAmount, 0);
    }
  }, [redeemList]);

  const handleRedeemApprove = useCallback(async () => {
    setError(null);

    if (redeemList[0]?.userId == null) {
      setError("User id not found");
      return;
    }
    const params = {
      code: redeemCode,
      amount: redeemAmount,
      userId: redeemList[0].userId,
    };

    try {
      const response = await approveRedeemCode(params);
      if (response.length > 0 && response.length == redeemList.length) {
        setError("Redeem code approved successfully");
        window.setTimeout(() => {
          setRedeemCode("");
          setRedeemList([]);
          setError(null);
        }, 10000);
      }
    } catch (error) {
      setError(error.message);
      window.setTimeout(() => {
        setRedeemCode("");
      }, 5000);
    }
  }, [redeemList, redeemCode, redeemAmount]);

  return (
    <div>
      <div className="flex flex-row gap-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-1/2 justify-between">
          <div className="flex flex-col p-4">
            <div className="gap-4 p-4 bg-green-600 rounded-lg text-white shadow">
              <div>
                <h3 className="text-2xl font-bold">Monthly transaction</h3>
                <h4 className="text-sm font-light">total</h4>
              </div>
              <div className="text-xl font-semibold text-gray-100">
                € {monthlyTransaction}
              </div>
            </div>
          </div>

          <div className="flex flex-col p-4">
            <div className="gap-4 p-4 bg-yellow-600 rounded-lg text-white shadow">
              <div>
                <h3 className="text-2xl font-bold">Total Redeem</h3>
                <h4 className="text-sm font-light">this month</h4>
              </div>
              <div className="text-xl font-semibold text-gray-100">
                € {monthlyRedeemAmount}
              </div>
            </div>
          </div>

          <div className="flex flex-col p-4">
            <div className="gap-4 p-4 bg-purple-600 rounded-lg text-white shadow">
              <div>
                <h3 className="text-2xl font-bold">Total Transactions</h3>
                <h4 className="text-sm font-light">this month</h4>
              </div>
              <div className="text-xl font-semibold text-gray-100">
                {monthlyTransactionCount}
              </div>
            </div>
          </div>

          <div className="flex flex-col p-4">
            <div className="gap-4 p-4 bg-blue-600 rounded-lg text-white shadow">
              <div>
                <h3 className="text-2xl font-bold">Total Redeems</h3>
                <h4 className="text-sm font-light">this month</h4>
              </div>
              <div className="text-xl font-semibold text-gray-100">
                {monthlyRedeemCount}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-start items-start gap-4 w-1/2 bg-gray-100 border-[1px] border-gray-200 p-8 my-4 rounded-lg shadow">
          <h3 className="text-2xl font-bold w-full">Search Bonus Code</h3>

          {error != null ? (
            <div className="text-red-600 bg-red-100 text-sm mb-2 w-full p-3 rounded">
              {error}
            </div>
          ) : null}

          <div className="flex flex-row gap-4 w-full">
            <input
              type="text"
              className="flex-1 p-2 rounded-md border border-gray-300 outline-none"
              value={redeemCode}
              onChange={handleRedeemCodeChange}
              placeholder="Search..."
            />
            <button
              className="bg-green-700 text-white px-4 py-2 rounded-md hover:bg-green-800"
              type="button"
              onClick={handleRedeemCodeSearch}
            >
              <QrCodeIcon className="w-5 h-5 inline-block mr-2" />
              Search
            </button>
          </div>
          {redeemList.length > 0 && (
            <div className="flex flex-col w-full mt-4">
              <table className="table w-full text-sm text-center">
                <thead>
                  <tr className="border-b border-b-gray-200">
                    <th className="py-2">User</th>
                    <th>Amount</th>
                    <th>Redeemed On</th>
                    <th>Expires On</th>
                  </tr>
                </thead>
                <tbody>
                  {redeemList.map((redeem) => (
                    <tr key={redeem.id} className="border-b border-b-gray-200">
                      <td className="py-2">{redeem.user}</td>
                      <td>€{redeem.receivableAmount}</td>
                      <td>
                        {redeem.redeemedOn
                          ? formatAppDate(redeem.redeemedOn)
                          : "-"}
                      </td>
                      <td>
                        {redeem.expiryDate
                          ? formatAppDate(redeem.expiryDate)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan="5"
                      className="py-2 text-right font-bold px-2 border-b border-b-gray-200"
                    >
                      Total Bonus: €{redeemAmount}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <div className="flex flex-row justify-end">
                <button
                  type="button"
                  className="px-4 py-2 mt-4 bg-red-500 text-white rounded-md hover:bg-red-700"
                  onClick={handleRedeemApprove}
                >
                  <CheckCircleIcon className="w-5 h-5 inline-block mr-2" />
                  Approve
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-12 justify-between mt-12">
        <div className="flex flex-col w-full lg:w-1/2 gap-4 p-4 rounded-lg shadow">
          <div>
            <h3 className="text-2xl font-bold">Daily Transaction</h3>
            <h4 className="text-sm font-light">last 7 days</h4>
          </div>
          <div className="h-80 w-full">
            {daywiseTransactionDataset !== null ? (
              <Bar options={options} data={daywiseTransactionDataset} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-col w-full lg:w-1/2 gap-4 p-4 rounded-lg shadow">
          <div>
            <h3 className="text-2xl font-bold">Users by Milestones</h3>
            <h4 className="text-sm font-light">this month</h4>
          </div>
          <div className="h-80 w-full justify-center items-center">
            {milestoneUserDataset !== null ? (
              <Bar options={options} data={milestoneUserDataset} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
